import { createPayment } from 'actions/entities/payments/payments';
import classNames from 'classnames';
import { BackButton } from 'components/BackButton/BackButton';
import { PausedError } from 'components/PausedError/PausedError';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { Alert } from 'components/ui/Alert/Alert';
import { Button } from 'components/ui/Button/Button';
import { COUNTRY_MISMATCH_ERROR, PAUSED_ERROR } from 'constants/errors';
import { Amount } from 'containers/Amount/Amount';
import { useAccountant } from 'hooks/useAccountant/useAccountant';
import { useFlysiteUrls } from 'hooks/useFlysiteUrls/useFlysiteUrls';
import { useDispatch, useSelector } from 'react-redux';
import {
  amountFromWithoutFees,
  amountTo,
  getExtraFees,
  getItems,
  getSelectedOfferExchangeRate,
  getSelectedOfferFees,
  getTaxes,
} from 'selectors';
import { getRecipientName } from 'selectors/entities/recipient/recipient';
import {
  getCreatePaymentError,
  isCreditCardPaymentOrder,
} from 'selectors/ui/ui';
import { createMarkup, useTranslations } from 'utils';
import { getCurrencyDisplayName } from 'utils/currency/currency';

const PaymentFailureAlert = ({ error }: { error: string }) => {
  const i18n = useTranslations();
  const { contactUrl } = useFlysiteUrls();

  if (error === COUNTRY_MISMATCH_ERROR) {
    return (
      <Alert
        type="danger"
        className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-gray-800 text-sm rounded"
      >
        <h3 className="font-medium text-xl pb-1 m-0">
          {i18n.t('checkout.error.title')}
        </h3>
        <span>{i18n.t('notifications.country_mismatch_error')}</span>
      </Alert>
    );
  }

  return (
    <Alert
      type="danger"
      className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-gray-800 text-sm rounded"
    >
      <h3 className="font-medium text-xl pb-1 m-0">
        {i18n.t('checkout.error.title')}
      </h3>
      <span
        dangerouslySetInnerHTML={createMarkup(
          i18n.t('checkout.error.message', { contactUrl }),
        )}
      />
    </Alert>
  );
};

interface Props {
  onNext: () => void;
  onPrev: () => void;
}

const ReviewAndConfirm = ({ onNext, onPrev }: Props) => {
  const dispatch = useDispatch();
  const i18n = useTranslations();
  const recipient = useSelector(getRecipientName);
  const amountWithoutFees = useSelector(amountFromWithoutFees);
  const recipientAmount = useSelector(amountTo);
  const fees = useSelector(getSelectedOfferFees);
  const extraFees = useSelector(getExtraFees);
  const taxes = useSelector(getTaxes);
  const items = useSelector(getItems);
  const createPaymentError = useSelector(getCreatePaymentError);
  const skipPaymentCreation = useSelector(isCreditCardPaymentOrder);

  const {
    total,
    totalFlywireFees,
    totalInternationalFees,
    totalTaxes,
    totalWithTaxesAndFees,
  } = useAccountant({
    amount: amountWithoutFees,
    fees,
    extraFees,
    taxes,
  });

  const payerCurrency = amountWithoutFees.currency as string;
  const recipientCurrency = recipientAmount.currency;

  const handleContinueClick = async () => {
    if (skipPaymentCreation) {
      onNext();
      return;
    }

    try {
      await dispatch(createPayment());
      onNext();
    } catch (_error) {
      // does nothing
    }
  };

  const shouldDisplayBreakdown =
    totalFlywireFees > 0 || totalInternationalFees > 0 || totalTaxes > 0;

  if (createPaymentError === PAUSED_ERROR) {
    return <PausedError />;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1">
        <BackButton onClick={onPrev} className="text-sm" />
        <h2 className="text-2xl font-semibold">
          {i18n.t('reviewAndConfirm.title')}
        </h2>

        {createPaymentError && createPaymentError !== PAUSED_ERROR && (
          <PaymentFailureAlert error={createPaymentError} />
        )}
        <dl>
          {shouldDisplayBreakdown && (
            <>
              <div className="grid w-full grid-cols-2 text-sm">
                <dt className="text-sm font-normal text-gray-700">
                  {i18n.t('reviewAndConfirm.originalAmount')}
                </dt>
                <dd className="justify-self-end text-base" data-testid="total">
                  <Amount value={total} currency={payerCurrency} />
                </dd>

                {totalFlywireFees > 0 && (
                  <>
                    <dt className="text-sm font-normal text-gray-700">
                      {i18n.t('reviewAndConfirm.fees')}
                    </dt>
                    <dd className="justify-self-end text-base">
                      <Amount
                        value={totalFlywireFees}
                        currency={payerCurrency}
                      />
                    </dd>
                  </>
                )}

                {totalInternationalFees > 0 && (
                  <>
                    <dt className="text-sm font-normal text-gray-700">
                      {i18n.t('reviewAndConfirm.internationalFees')}
                    </dt>
                    <dd className="justify-self-end text-base">
                      <Amount
                        value={totalInternationalFees}
                        currency={payerCurrency}
                      />
                    </dd>
                  </>
                )}

                {totalTaxes > 0 && (
                  <>
                    <dt className="text-sm font-normal text-gray-700">
                      {i18n.t('reviewAndConfirm.taxes')}
                    </dt>
                    <dd className="justify-self-end text-base">
                      <Amount value={totalTaxes} currency={payerCurrency} />
                    </dd>
                  </>
                )}
              </div>
              <hr className="my-4 h-px border-0 bg-gray-300" />
            </>
          )}

          <div className="grid w-full grid-cols-2 items-center gap-y-6 text-sm">
            <dt className="text-sm font-normal text-gray-700">
              {i18n.t('reviewAndConfirm.totalAmount')}
            </dt>
            <dd
              className="mb-0 justify-self-end text-xl font-semibold"
              data-testid="totalWithTaxesAndFees"
            >
              <Amount value={totalWithTaxesAndFees} currency={payerCurrency} />
            </dd>
            <ExchangeRate />
          </div>

          <hr className="my-4 h-px border-0 bg-gray-300" />

          <div className="grid w-full grid-cols-2 items-center gap-y-6 text-sm">
            <dt className="text-sm font-normal text-gray-700">
              {i18n.t('reviewAndConfirm.recipientAmount', {
                recipient: recipient as string,
              })}
            </dt>
            <dd className="mb-0 justify-self-end text-xl font-semibold">
              <Amount
                value={recipientAmount.value}
                currency={recipientCurrency}
              />
            </dd>
          </div>

          {items.length > 1 &&
            items.map((item, index) => (
              <div
                className={classNames(
                  'ml-6 flex items-center justify-between mt-2',
                  index === items.length - 1 ? 'mb-7' : '',
                )}
                key={item.id}
              >
                <dt className="text-sm font-normal text-gray-700">
                  {item.label}
                </dt>
                <dd className="text-[13px] font-normal text-gray-700 !mb-0">
                  (
                  <Amount
                    value={item.defaultValue as number}
                    currency={recipientCurrency}
                  />
                  )
                </dd>
              </div>
            ))}
        </dl>
      </div>
      <div className="pt-6 pb-4">
        <Button
          className="uppercase"
          tone="primary"
          size="full"
          onClick={handleContinueClick}
          data-testid="confirmButton"
        >
          {i18n.t('reviewAndConfirm.continueButton')}
        </Button>
      </div>
    </div>
  );
};

export { ReviewAndConfirm };

const ExchangeRate = () => {
  const i18n = useTranslations();
  const exchangeRate = useSelector(getSelectedOfferExchangeRate);
  if (!exchangeRate.value) {
    return null;
  }
  const currencyFrom = getCurrencyDisplayName(exchangeRate.currencyFrom);
  const currencyTo = getCurrencyDisplayName(exchangeRate.currencyTo);

  let inverseExchangeRateValue: number | null = 0;
  if (exchangeRate.value !== 0) {
    inverseExchangeRateValue = 1 / exchangeRate.value;
  }

  const hasValidExchangeRate =
    exchangeRate.value !== 0 || inverseExchangeRateValue !== 0;
  const isDifferentCurrency =
    exchangeRate.currencyFrom.code !== exchangeRate.currencyTo.code;
  const shouldDisplayExchangeRate = hasValidExchangeRate && isDifferentCurrency;

  if (!shouldDisplayExchangeRate) {
    return null;
  }

  const rateFormat = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 5,
  });

  return (
    <>
      <dt className="flex items-center gap-x-2 font-normal text-gray-700">
        {i18n.t('reviewAndConfirm.exchangeRate')}{' '}
        <Tooltip.Info
          data-testid="exchante-rate-tooltip"
          className="text-sm font-medium"
        >
          {i18n.t('reviewAndConfirm.exchangeRate.tooltip')}
        </Tooltip.Info>
      </dt>
      <dd className="flex flex-col justify-self-end text-end text-base">
        {exchangeRate.value !== 0 && (
          <span className="">
            {i18n.t('reviewAndConfirm.exchangeRateFromTo', {
              currencyFrom,
              currencyTo,
              value: rateFormat.format(exchangeRate.value),
            })}
          </span>
        )}
        {inverseExchangeRateValue !== 0 && (
          <span className="">
            {i18n.t('reviewAndConfirm.exchangeRateToFrom', {
              currencyFrom,
              currencyTo,
              value: rateFormat.format(inverseExchangeRateValue),
            })}
          </span>
        )}
      </dd>
    </>
  );
};
